import type { SelfPctileEntry } from './types';

interface ThesisProps {
  sym: string;
  Q: number | null;
  V: number | null;
  C: number | null;
  mix: { qShare: number; vShare: number } | null;
  selfPctile: SelfPctileEntry | undefined;
  d20: number | null;
  alphaStreak: number | null;
  fiveYrAnn: number | null;
  streetUpside: number | null;
  rating: number | null;
  asymmetry: number | null;
  hedgeActive: boolean;
  tierLabel: string;
  topFactorLabels: string[];
}

export function ThesisProse(p: ThesisProps) {
  const tilt = (() => {
    if (!p.mix) return null;
    if (p.mix.qShare >= 0.85) return 'pure-quality bet — valuation is essentially a non-factor';
    if (p.mix.qShare >= 0.65) return 'Q-driven with secondary V support';
    if (p.mix.qShare >= 0.35) return 'balanced Q + V';
    return 'V-driven (deep-value) play with adequate quality';
  })();

  const qPct = p.selfPctile?.q?.percentile ?? null;
  const vPct = p.selfPctile?.v?.percentile ?? null;
  const qRegime = qPct == null ? null
    : qPct >= 85 ? `quality is at the top of its multi-year range (${qPct.toFixed(0)}% own-history)`
    : qPct >= 60 ? `quality is above its own historical median (${qPct.toFixed(0)}%)`
    : qPct >= 40 ? 'quality is near its own historical median'
    : qPct >= 20 ? `quality is below its own historical median (${qPct.toFixed(0)}%) — watch for further fade`
    : `quality is near its own multi-year low (${qPct.toFixed(0)}%) — turnaround setup`;
  const vRegime = vPct == null ? null
    : vPct >= 85 ? `valuation is the cheapest it's been in years (${vPct.toFixed(0)}% own-history)`
    : vPct >= 60 ? `valuation is cheaper than its own historical norm (${vPct.toFixed(0)}%)`
    : vPct >= 40 ? 'valuation is roughly fair vs its own range'
    : `valuation is richer than its own norm (${vPct.toFixed(0)}% own-history — paying up)`;

  const factorPhrase = p.topFactorLabels.length === 0 ? null
    : `Top contributors: ${p.topFactorLabels.join(', ')}.`;

  const horizonPhrase = (() => {
    if (p.fiveYrAnn == null) return null;
    const modelSign = p.fiveYrAnn >= 0 ? '+' : '';
    const asymWord = p.asymmetry == null ? ''
      : p.asymmetry >= 4 ? ' (asymmetry well-cleared)'
      : p.asymmetry >= 2 ? ' (asymmetry adequate)'
      : ' (asymmetry below entry bar — model is cautious)';
    let s = `Model 5y view: ${modelSign}${p.fiveYrAnn.toFixed(1)}%/yr${asymWord}.`;
    if (p.streetUpside != null) {
      const streetSign = p.streetUpside >= 0 ? '+' : '';
      s += ` Street 12mo: ${streetSign}${p.streetUpside.toFixed(0)}%.`;
    }
    return s;
  })();

  const hedgePhrase = p.hedgeActive
    ? 'Hedge signal active — model sees asymmetric downside building + 12-month momentum extended. Overlay a collar via /hedge-lab; do not exit.'
    : null;

  const streakPhrase = p.alphaStreak != null && p.alphaStreak > 0
    ? `In the Alpha Zone for ${p.alphaStreak} consecutive day${p.alphaStreak === 1 ? '' : 's'}.`
    : null;

  const lead = `Composite ${p.C?.toFixed(2) ?? '—'} (${p.tierLabel}).${tilt ? ` This is a ${tilt}.` : ''}`;
  const regime = qRegime && vRegime
    ? `${qRegime[0].toUpperCase() + qRegime.slice(1)}; ${vRegime}.`
    : null;

  const lines = [lead, regime, factorPhrase, horizonPhrase, hedgePhrase, streakPhrase].filter(Boolean);

  return (
    <div className="space-y-1.5">
      {lines.map((l, i) => <p key={i}>{l}</p>)}
    </div>
  );
}
